import { useCallback, useRef } from "react";
import { useBreakpoint, Tooltip } from "@avenue-8/sidekick-ui";
import styled from "@emotion/styled";
import { SwitchTransition, CSSTransition } from "react-transition-group";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { Box, Container, Divider, IconButton, Skeleton } from "@mui/material";
import { useGetThreads } from "@queries/chat/useGetThreads";
import { useChatWidgetLogic } from "@shared/hooks/useChatWidgetLogic";
import { User, UserRoles } from "@store/auth.store";
import { useThreadsStore } from "@store/threads.store";
import { useUIStore } from "@store/ui.store";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { useAppLogoAsset } from "@shared/hooks/useAppLogoAsset";
import { BlockedThreads } from "./components/BlockedThreads";
import { NewChatButton } from "./components/NewChatButton";
import { useChatSidebarNavItems } from "./useChatSidebarNavItems";
import { Threads } from "./components/Threads";
import { UserAvatarMenu } from "./components/UserAvatarMenu";
import {
  ChatSidebarDrawer,
  CollapsedExpandIconOverlay,
  CollapsedFooter,
  CollapsedLogoButton,
  CollapsedLogoImage,
  CollapsedNavComingSoonItem,
  CollapsedNavItemWrapper,
  CollapsedNavLink,
  CollapsedNavList,
  CollapsedRailContainer,
  CollapsedSpacer,
  ComingSoonLabel,
  DrawerContainer,
  SidebarFooter,
  SidebarHeader,
  SidebarNav,
  SidebarNavComingSoonItem,
  SidebarNavComingSoonItemContent,
  SidebarNavItem,
  SidebarNavLink,
} from "./styles";
import { AppLogo } from "../AppLogo";

type Props = {
  currentThread: string | undefined;
  navigateToThread: (id: string | null) => void;
  onNewChatClick: () => void;
  userAvatar: string | null;
  userFirstName: string;
  userName: string;
  userEmail: string;
  userRoles: UserRoles[];
  userSubscriptionStatus: User["subscriptionStatus"];
};

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const ChatSidebar = (props: Props) => {
  const {
    currentThread,
    navigateToThread,
    onNewChatClick,
    userAvatar,
    userFirstName,
    userName,
    userEmail,
    userRoles,
    userSubscriptionStatus,
  } = props;
  const setThreads = useThreadsStore((state) => state.setThreads);
  const isSidebarOpen = useUIStore((state) => state.isSidebarOpen);
  const isSidebarOpenDesktop = useUIStore(
    (state) => state.isSidebarOpenDesktop,
  );
  const toggleSidebarDesktop = useUIStore(
    (state) => state.toggleSidebarDesktop,
  );
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const messageInputText = useUIStore((state) => state.messageInputText);
  const openSettingsModal = useUIStore((state) => state.openSettingsModal);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isLaptopDown = useBreakpoint("laptop");
  const collapsedNodeRef = useRef<HTMLDivElement>(null);
  const expandedNodeRef = useRef<HTMLDivElement>(null);
  const { chatWidget, resetChatWidget } = useChatWidgetLogic();
  const smallRoundedLogo = useAppLogoAsset("smallRoundedBlue");

  const threadsQuery = useGetThreads({
    enabled: userSubscriptionStatus === "active",
    onSuccess: (data) => {
      setThreads(data.threads);
    },
  });

  const onLogout = () => {
    navigate("/logout");
  };

  const onSettingsClick = () => {
    openSettingsModal("integrations");
  };

  const isLoading = threadsQuery.isLoading;
  // Desktop never fully hides anymore — it just switches between the
  // expanded drawer and the collapsed icon rail (see isDesktopCollapsed).
  const open = isLaptopDown ? isSidebarOpen : true;
  const onClose = isLaptopDown
    ? () => toggleSidebar(false)
    : () => toggleSidebarDesktop(false);
  const isDesktopCollapsed = !isLaptopDown && !isSidebarOpenDesktop;

  const onSidebarNavLinkClick = useCallback(() => {
    if (chatWidget?.temporary) {
      resetChatWidget();
    }
    if (isLaptopDown) toggleSidebar(false);
  }, [isLaptopDown, toggleSidebar, chatWidget, resetChatWidget]);

  const sidebarNavItems = useChatSidebarNavItems();

  const handleNewChatClick = () => {
    onNewChatClick();
    messageInputText?.focus();
    toggleSidebar(false);
  };

  if (isLoading) {
    return <ChatSidebarSkeleton />;
  }

  const collapsedContent = (
    <CollapsedRailContainer ref={collapsedNodeRef}>
      <Tooltip title="Expand sidebar" placement="right">
        <CollapsedLogoButton
          onClick={() => toggleSidebarDesktop(true)}
          aria-label="Expand sidebar"
        >
          <CollapsedLogoImage
            className="logo-image"
            src={smallRoundedLogo.src}
            alt={smallRoundedLogo.alt}
          />
          <CollapsedExpandIconOverlay className="expand-icon">
            <MenuOpenIcon sx={{ transform: "rotate(180deg)" }} />
          </CollapsedExpandIconOverlay>
        </CollapsedLogoButton>
      </Tooltip>

      <UserAvatarMenu
        userAvatar={userAvatar}
        userFirstName={userFirstName}
        userName={userName}
        userEmail={userEmail}
        userRoles={userRoles}
        onLogout={onLogout}
        onSettingsClick={onSettingsClick}
      />

      <Divider flexItem sx={{ width: "100%" }} />

      <CollapsedNavList>
        {sidebarNavItems.map((item) => {
          const Icon = item.icon;

          if (item.comingSoon) {
            return (
              <Tooltip
                key={item.id}
                title={`${item.label} — Coming soon`}
                placement="right"
              >
                <CollapsedNavComingSoonItem aria-label={item.label}>
                  <Icon fontSize="small" />
                </CollapsedNavComingSoonItem>
              </Tooltip>
            );
          }

          const isActive = !!matchPath(
            { path: item.to, end: item.exact ?? false },
            pathname,
          );

          return (
            <Tooltip key={item.id} title={item.label} placement="right">
              <CollapsedNavItemWrapper $active={isActive}>
                <CollapsedNavLink
                  to={item.to}
                  aria-label={item.label}
                  onClick={onSidebarNavLinkClick}
                >
                  <Icon fontSize="small" />
                </CollapsedNavLink>
              </CollapsedNavItemWrapper>
            </Tooltip>
          );
        })}
      </CollapsedNavList>

      <CollapsedSpacer />

      <CollapsedFooter>
        <NewChatButton
          collapsed
          onClick={handleNewChatClick}
          disabled={userSubscriptionStatus !== "active"}
        />
      </CollapsedFooter>
    </CollapsedRailContainer>
  );

  const expandedContent = (
    <DrawerContainer ref={expandedNodeRef}>
      <SidebarHeader>
        <HeaderLeft>
          {open && (
            <IconButton onClick={onClose} size="small" color="primary">
              <MenuOpenIcon />
            </IconButton>
          )}
          <div onClick={onSidebarNavLinkClick}>
            <AppLogo size={{ sidekick: "xs", radar: "md" }} padding="0" />
          </div>
        </HeaderLeft>

        <UserAvatarMenu
          userAvatar={userAvatar}
          userFirstName={userFirstName}
          userName={userName}
          userEmail={userEmail}
          userRoles={userRoles}
          onLogout={onLogout}
          onSettingsClick={onSettingsClick}
        />
      </SidebarHeader>
      <SidebarNav>
        {sidebarNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = !!matchPath(
            { path: item.to, end: item.exact ?? false },
            pathname,
          );

          if (item.comingSoon) {
            return (
              <SidebarNavComingSoonItem key={item.id}>
                <SidebarNavComingSoonItemContent>
                  <Icon fontSize="small" />
                  {item.label}
                </SidebarNavComingSoonItemContent>
                <ComingSoonLabel variant="body2">Coming soon</ComingSoonLabel>
              </SidebarNavComingSoonItem>
            );
          }

          return (
            <SidebarNavItem key={item.id} $active={isActive} role="button">
              <SidebarNavLink
                key={item.id}
                to={item.to}
                onClick={onSidebarNavLinkClick}
              >
                <Icon fontSize="small" />
                {item.label}
              </SidebarNavLink>
            </SidebarNavItem>
          );
        })}
        <Divider sx={{ mt: 2 }} />
      </SidebarNav>
      {userSubscriptionStatus === "active" ? (
        <Threads
          currentThread={currentThread}
          navigateToThread={navigateToThread}
        />
      ) : (
        <BlockedThreads userSubscriptionStatus={userSubscriptionStatus} />
      )}

      <SidebarFooter>
        <NewChatButton
          onClick={handleNewChatClick}
          disabled={userSubscriptionStatus !== "active"}
        />
      </SidebarFooter>
    </DrawerContainer>
  );

  return (
    <ChatSidebarDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      variant={isLaptopDown ? "temporary" : "persistent"}
      className={
        isLaptopDown || isSidebarOpenDesktop ? "sidebar-expanded" : undefined
      }
      id="sidekick-chat-sidebar"
    >
      <SwitchTransition mode="out-in">
        <CSSTransition
          key={isDesktopCollapsed ? "collapsed" : "expanded"}
          nodeRef={isDesktopCollapsed ? collapsedNodeRef : expandedNodeRef}
          timeout={130}
          classNames="sidebar-content"
          appear={false}
        >
          {isDesktopCollapsed ? collapsedContent : expandedContent}
        </CSSTransition>
      </SwitchTransition>
    </ChatSidebarDrawer>
  );
};

export const ChatSidebarSkeleton = () => {
  const isLaptopDown = useBreakpoint("laptop");
  const isSidebarOpen = useUIStore((state) => state.isSidebarOpen);
  const isSidebarOpenDesktop = useUIStore(
    (state) => state.isSidebarOpenDesktop,
  );
  const isCollapsed = !isLaptopDown && !isSidebarOpenDesktop;

  return (
    <ChatSidebarDrawer
      anchor="left"
      open={isLaptopDown ? isSidebarOpen : true}
      variant={isLaptopDown ? "temporary" : "persistent"}
      className={
        isLaptopDown || isSidebarOpenDesktop ? "sidebar-expanded" : undefined
      }
      id="sidekick-chat-sidebar"
    >
      {isCollapsed ? (
        <CollapsedRailContainer>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="circular" width={40} height={40} />
          <Divider flexItem sx={{ width: "100%" }} />
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1}
            width="100%"
          >
            <Skeleton variant="rounded" width={40} height={40} />
            <Skeleton variant="rounded" width={40} height={40} />
            <Skeleton variant="rounded" width={40} height={40} />
          </Box>
          <CollapsedSpacer />
          <Skeleton variant="circular" width={40} height={40} />
        </CollapsedRailContainer>
      ) : (
        <DrawerContainer>
          <SidebarHeader>
            <HeaderLeft>
              <Skeleton variant="rounded" width={36} height={36} />
              <AppLogo size={{ sidekick: "xs", radar: "md" }} padding="0" />
            </HeaderLeft>
            <Skeleton variant="circular" width={40} height={40} />
          </SidebarHeader>

          <SidebarNav>
            <Box width="100%" px={3} py={1}>
              <Skeleton height={80} width="100%" variant="rounded" />
            </Box>
            <Divider sx={{ mt: 2 }} />
          </SidebarNav>

          <Container sx={{ paddingBottom: 2 }}>
            <Skeleton variant="rounded" width="100%" height="100%" />
          </Container>

          <SidebarFooter>
            <Skeleton height={46} width="100%" variant="rounded" />
          </SidebarFooter>
        </DrawerContainer>
      )}
    </ChatSidebarDrawer>
  );
};
